/* eslint-disable max-lines */
import {GRANITE_10} from '@gaia/colors';
import Button from '@gaia/components/Button';
import useKeypresses from '@gaia/hooks/useKeypress';
import usePersistentState from '@gaia/hooks/usePersistentState';
import theme from '@gaia/theme';
import createTrackPoint from '@gaia/utils/gaia/createTrackPoint';
import {getTrackLineRequests} from '@gaia/utils/gaia/getTrackLineRequests';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {isMac} from '@gaia/utils/queryNavigator';
import {
    SidebarAwareCenteredSnackbar,
    SidebarAwareCenteredSnackbarProps,
} from '@map/snackbars/SidebarAwareCenteredSnackbar';
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Portal,
    Tooltip,
    Typography,
} from '@mui/material';
import along from '@turf/along';
import {lineString, Units} from '@turf/helpers';
import length from '@turf/length';
import isEqual from 'lodash/isEqual';
import {LngLat} from 'mapbox-gl';
import {RefObject, useCallback, useEffect, useReducer, useState} from 'react';

import BackToStartIcon from './back-to-start.svg?react';
import OutAndBackIcon from './out-and-back.svg?react';
import RedoIcon from './redo.svg?react';
import ReverseIcon from './reverse.svg?react';
import UndoIcon from './undo.svg?react';

const minBackToStartDistance = 50; // meters
export const backToStartButtonText = 'Back to Start';

type InteractionInstructionsSnackbarProps = SidebarAwareCenteredSnackbarProps & {
    interaction: EditingRouteInteractionState;
    onInteractionChange: SetState<InteractionState>;
    project: MapProject;
    mapSurfaceContainerRef: RefObject<HTMLDivElement>;
};

export type TrackWithPoints = Track & {
    isGeometryLoaded: true;
};

type UndoState = {
    past: TrackWithPoints[];
    present: TrackWithPoints;
    future: TrackWithPoints[];
};

type UndoAction =
    | {
          type: 'undo' | 'redo';
      }
    | {
          type: 'addToPast';
          present: TrackWithPoints;
      };

function getModifierKey() {
    return isMac() ? '⌘' : 'Ctrl';
}

function getFirstAndLastPoint(points: TrackPoint[]): [GeoJSON.Position, GeoJSON.Position] {
    if (points.length < 2) {
        throw new Error('Not enough points!');
    }

    return [
        points.reduce((min, p) => (p.properties.order < min.properties.order ? p : min)).geometry
            .coordinates,
        points.reduce((max, p) => (p.properties.order > max.properties.order ? p : max)).geometry
            .coordinates,
    ];
}

function getPointToPointDistance(
    points: [GeoJSON.Position, GeoJSON.Position],
    units: Units = 'meters'
) {
    return length(lineString(points), {units});
}

export function trackHistoryReducer(trackHistory: UndoState, action: UndoAction): UndoState {
    switch (action.type) {
        case 'undo': {
            const past = [...trackHistory.past];
            const newPresent = past.pop();

            if (!newPresent) return trackHistory;

            const nextUndoState = {
                past,
                present: newPresent,
                future: [...trackHistory.future, trackHistory.present],
            };

            return nextUndoState;
        }

        case 'redo': {
            const [present] = trackHistory.future.slice(-1);

            if (!present) return trackHistory;

            const nextUndoState = {
                past: [...trackHistory.past, trackHistory.present],
                present,
                future: trackHistory.future.slice(0, -1),
            };

            return nextUndoState;
        }

        case 'addToPast': {
            if (isEqual(action.present.points, trackHistory.present.points)) return trackHistory;
            const nextUndoState = {
                past: [...trackHistory.past, trackHistory.present],
                present: action.present,
                future: [],
            };

            return nextUndoState;
        }
    }
}

export function reverseTrackPoints(points: {[id: string]: TrackPoint}): {
    [id: string]: TrackPoint;
} {
    return Object.values(points)
        .filter((p) => !p.properties.archived)
        .sort((a, b) => b.properties.order - a.properties.order)
        .map((p, i) => ({
            ...p,
            properties: {...p.properties, order: i},
        }))
        .reduce((acc, cur) => {
            return {
                ...acc,
                [cur.properties.localId]: cur,
            };
        }, {});
}

function assertTrackWithPoints(t: any): asserts t is TrackWithPoints {
    if (!t.isGeometryLoaded) {
        throw new Error('Not a Track with Points!');
    }
}

async function getCurrentTrackLines(
    interaction: EditingRouteInteractionState,
    project: MapProject
) {
    const trackLines = await Promise.all(
        getTrackLineRequests({
            interaction,
            track: interaction.track,
            project,
        })
            .filter((line): line is AsyncTrackLineRequest => line.type === 'async')
            .map((line) => line.geometry)
    );

    if (trackLines.length === 0) {
        return undefined;
    }

    return trackLines;
}

export default function RouteEditingSnackbar({
    interaction,
    onInteractionChange,
    project,
    mapSurfaceContainerRef,
    className,
    mapControlsOffset,
}: InteractionInstructionsSnackbarProps) {
    const {subtype, track} = interaction;

    assertTrackWithPoints(track);

    const activePoints = Object.values(track.points)
        .filter((p) => !p.properties.archived)
        .sort((a, b) => a.properties.order - b.properties.order);

    const isNewRoute = track.properties.id === undefined;

    const initialState = {
        past: [],
        present: track,
        future: [],
    };

    const [trackHistory, dispatch] = useReducer(trackHistoryReducer, initialState);

    // Watches the interaction object for new track control points
    // Points are not updated during dragging, only on drag end
    useEffect(() => {
        if (subtype === 'draggingPoint') return;
        dispatch({type: 'addToPast', present: track});
    }, [subtype, track]);

    // Update the interaction state with the latest track control points after
    // the user uses a route editing tool
    useEffect(() => {
        onInteractionChange((oldInteraction) => ({
            ...oldInteraction,
            track: trackHistory.present,
        }));
    }, [onInteractionChange, trackHistory]);

    const [busy, setBusy] = useState(false);

    const isBackToStartDisabled =
        busy ||
        activePoints.length < 2 ||
        getPointToPointDistance(getFirstAndLastPoint(activePoints)) < minBackToStartDistance;

    const isOutAndBackDisabled =
        busy ||
        activePoints.length < 2 ||
        getPointToPointDistance(getFirstAndLastPoint(activePoints)) < minBackToStartDistance;

    const isReverseDisabled = busy || activePoints.length < 2;

    const isUndoDisabled = busy || trackHistory.past.length === 0;

    const isRedoDisabled = busy || trackHistory.future.length === 0;

    const backToStart = useCallback(
        async (triggeredByKeyboardShortcut = false) => {
            try {
                setBusy(true);

                const trackLines = await getCurrentTrackLines(interaction, project);

                let endPointLngLat: LngLat;
                if (trackLines === undefined) {
                    endPointLngLat = new LngLat(
                        ...(activePoints[0].geometry.coordinates as [number, number])
                    );
                } else {
                    const [firstTrackLine] = trackLines;

                    const endPoint = along(firstTrackLine, 0.01, {
                        units: 'kilometers',
                    });

                    endPointLngLat = new LngLat(
                        ...(endPoint.geometry.coordinates as [number, number])
                    );
                }

                const updatedState = createTrackPoint({
                    lngLat: endPointLngLat,
                    track: interaction.track,
                });

                if (!updatedState) throw new Error("Can't create track point!");

                const [pointLocalId, updatedTrackState] = updatedState;

                onInteractionChange((oldInteraction) => {
                    if (oldInteraction.type !== 'editingRoute') {
                        return oldInteraction;
                    }
                    return {
                        ...oldInteraction,
                        pointLocalId,
                        subtype: 'appendingPoint',
                        idle: false,
                        cursorLngLat: endPointLngLat,
                        track: updatedTrackState,
                    };
                });
            } finally {
                setBusy(false);

                ampli.useRouteEditingTool({
                    routeEditingTool: 'backToStart',
                    routingMode: track.properties.routing_mode ?? 'unspecified',
                    isNewRoute,
                    trigger: triggeredByKeyboardShortcut ? 'keyboard shortcut' : 'button',
                });
            }
        },
        [
            activePoints,
            interaction,
            isNewRoute,
            onInteractionChange,
            project,
            track.properties.routing_mode,
        ]
    );

    async function outAndBack(triggeredByKeyboardShortcut = false) {
        try {
            if (!interaction.track.isGeometryLoaded) {
                return;
            }

            setBusy(true);

            const trackLines = await getCurrentTrackLines(interaction, project);

            let lngLats: LngLat[];

            // If the routing service is not available, we use the control points' coordinates
            if (!trackLines || activePoints.length - 1 !== trackLines.length) {
                lngLats = [...activePoints]
                    .reverse()
                    .map((p) => new LngLat(...(p.geometry.coordinates as [number, number])));
            } else {
                // Else the routing service is available, we offset the control points' coordinates along the track geometry
                lngLats = trackLines.reverse().map(
                    (trackLine) =>
                        new LngLat(
                            ...(along(trackLine, 0.01, {
                                units: 'kilometers',
                            }).geometry.coordinates as [number, number])
                        )
                );
            }

            const updatedTrack = lngLats.reduce((_updatedTrack: Track, lngLat) => {
                const createTrackPointResult = createTrackPoint({
                    lngLat,
                    track: _updatedTrack,
                });
                if (!createTrackPointResult) {
                    throw new Error("Can't create track point!");
                }
                const [, updatedTrackState] = createTrackPointResult;
                return updatedTrackState;
            }, interaction.track);
            onInteractionChange({
                ...interaction,
                track: updatedTrack,
            });
        } finally {
            setBusy(false);

            ampli.useRouteEditingTool({
                routeEditingTool: 'outAndBack',
                routingMode: track.properties.routing_mode ?? 'unspecified',
                isNewRoute,
                trigger: triggeredByKeyboardShortcut ? 'keyboard shortcut' : 'button',
            });
        }
    }

    const reverseRoute = useCallback(
        (triggeredByKeyboardShortcut = false) => {
            if (!interaction.track.isGeometryLoaded) {
                return;
            }
            const pointsInReverseOrder = reverseTrackPoints(interaction.track.points);

            ampli.useRouteEditingTool({
                routeEditingTool: 'reverse',
                routingMode: track.properties.routing_mode ?? 'unspecified',
                isNewRoute,
                trigger: triggeredByKeyboardShortcut ? 'keyboard shortcut' : 'button',
            });
            onInteractionChange({
                ...interaction,
                track: {
                    ...interaction.track,
                    points: pointsInReverseOrder,
                },
            });
        },
        [interaction, isNewRoute, onInteractionChange, track.properties.routing_mode]
    );

    const [autoOpenRouteEditingKeyboardShortcutsHelp] = usePersistentState(
        'autoOpenRouteEditingKeyboardShortcutsHelp',
        true
    );
    const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(
        autoOpenRouteEditingKeyboardShortcutsHelp
    );

    useKeypresses({
        'meta+z': () => {
            if (isUndoDisabled) return;
            dispatch({type: 'undo'});
            ampli.useRouteEditingTool({
                routeEditingTool: 'undo',
                routingMode: track.properties.routing_mode ?? 'unspecified',
                isNewRoute,
                trigger: 'keyboard shortcut',
            });
        },
        'meta+y': () => {
            if (isRedoDisabled) return;
            dispatch({type: 'redo'});
            ampli.useRouteEditingTool({
                routeEditingTool: 'redo',
                routingMode: track.properties.routing_mode ?? 'unspecified',
                isNewRoute,
                trigger: 'keyboard shortcut',
            });
        },
        b: () => {
            if (isBackToStartDisabled) return;
            backToStart(true).catch(console.error);
        },
        o: () => {
            if (isOutAndBackDisabled) return;
            outAndBack(true).catch(console.error);
        },
        r: () => {
            if (isReverseDisabled) return;
            reverseRoute(true);
        },
        '?': () => {
            setIsShortcutsHelpOpen((isOpen) => !isOpen);
        },
    });

    return (
        <>
            <SidebarAwareCenteredSnackbar
                className={className}
                open={true}
                mapControlsOffset={mapControlsOffset}
            >
                <Paper
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: GRANITE_10,
                        padding: theme.spacing(1),
                        borderRadius: theme.spacing(0.5),
                        alignItems: 'center',
                    }}
                    elevation={6}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            pointerEvents: busy ? 'none' : 'auto',
                        }}
                    >
                        <Tooltip title='Back to start (B)'>
                            <span>
                                <Button
                                    aria-label={backToStartButtonText}
                                    aria-keyshortcuts='b'
                                    onClick={() => backToStart()}
                                    disabled={isBackToStartDisabled}
                                    appearance='outlined'
                                    size='small'
                                    style={{marginRight: theme.spacing(1)}}
                                >
                                    <BackToStartIcon />
                                    {backToStartButtonText}
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title='Out and back (O)'>
                            <span>
                                <Button
                                    aria-label='out and back'
                                    aria-keyshortcuts='o'
                                    onClick={() => outAndBack()}
                                    disabled={isOutAndBackDisabled}
                                    appearance='outlined'
                                    size={'small'}
                                    style={{marginRight: theme.spacing(1)}}
                                >
                                    <OutAndBackIcon />
                                    Out &amp; Back
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title='Reverse route (R)'>
                            <span>
                                <Button
                                    aria-label='reverse route'
                                    aria-keyshortcuts='r'
                                    onClick={() => reverseRoute()}
                                    disabled={isReverseDisabled}
                                    appearance='outlined'
                                    size='small'
                                    style={{marginRight: theme.spacing(1)}}
                                >
                                    <ReverseIcon />
                                    Reverse
                                </Button>
                            </span>
                        </Tooltip>
                        <Tooltip title={`Undo (${getModifierKey()}+Z)`}>
                            <span>
                                <IconButton
                                    aria-label='undo'
                                    aria-keyshortcuts='meta+u'
                                    onClick={() => {
                                        dispatch({type: 'undo'});
                                        ampli.useRouteEditingTool({
                                            routeEditingTool: 'undo',
                                            routingMode:
                                                track.properties.routing_mode ?? 'unspecified',
                                            isNewRoute,
                                            trigger: 'button',
                                        });
                                    }}
                                    disabled={isUndoDisabled}
                                    size={'small'}
                                >
                                    <UndoIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={`Redo (${getModifierKey()}+Y)`}>
                            <span>
                                <IconButton
                                    aria-label='redo'
                                    aria-keyshortcuts='meta+y'
                                    onClick={() => {
                                        dispatch({type: 'redo'});
                                        ampli.useRouteEditingTool({
                                            routeEditingTool: 'redo',
                                            routingMode:
                                                track.properties.routing_mode ?? 'unspecified',
                                            isNewRoute,
                                            trigger: 'button',
                                        });
                                    }}
                                    disabled={isRedoDisabled}
                                    size={'small'}
                                >
                                    <RedoIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </Paper>
            </SidebarAwareCenteredSnackbar>
            {mapSurfaceContainerRef.current && (
                <Portal container={mapSurfaceContainerRef.current}>
                    <RouteEditingKeyboardShortcutsHelp
                        open={isShortcutsHelpOpen}
                        setIsOpen={setIsShortcutsHelpOpen}
                    />
                </Portal>
            )}
        </>
    );
}

interface RouteEditingKeyboardShortcutsHelpProps extends DialogProps {
    setIsOpen: (isOpen: boolean) => void;
}

function RouteEditingKeyboardShortcutsHelp({
    open,
    setIsOpen,
}: RouteEditingKeyboardShortcutsHelpProps) {
    const help = {
        [`${getModifierKey()}+Z`]: 'Undo',
        [`${getModifierKey()}+Y`]: 'Redo',
        B: 'Back to start',
        O: 'Out and back',
        R: 'Reverse route',
        '?': 'Open/Close this help dialog',
    };

    const [shouldOpenAutomatically, setShouldOpenAutomatically] = usePersistentState(
        'autoOpenRouteEditingKeyboardShortcutsHelp',
        true
    );

    return (
        <Dialog open={open}>
            <div
                style={{
                    maxWidth: 410,
                    minWidth: 230,
                }}
            >
                <DialogTitle>Shortcuts</DialogTitle>
                <DialogContent>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'max-content 1fr',
                            gridAutoRows: 'auto',
                            rowGap: theme.spacing(1),
                            columnGap: theme.spacing(2),
                        }}
                    >
                        {Object.entries(help)
                            .flat()
                            .map((text, i) => (
                                <Typography
                                    key={i}
                                    variant='body1'
                                    style={{
                                        textAlign: i % 2 === 0 ? 'right' : 'left', // Shortcuts are right-aligned
                                    }}
                                >
                                    {text}
                                </Typography>
                            ))}
                    </div>
                </DialogContent>
                <DialogActions>
                    <FormControlLabel
                        style={{marginLeft: theme.spacing(1)}}
                        control={
                            <Checkbox
                                checked={!shouldOpenAutomatically}
                                onChange={(e: any) => setShouldOpenAutomatically(!e.target.checked)}
                                color='default'
                            />
                        }
                        label={"Don't show this again"}
                    />
                    <Button onClick={() => setIsOpen(false)}>Close</Button>
                </DialogActions>
            </div>
        </Dialog>
    );
}
